import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, Monitor, LayoutGrid } from "lucide-react";
import toast from "react-hot-toast";
import PageHeader from "../../../components/shared/PageHeader";
import api from "../../../api";
import AiSeoNativeView from "./AiSeoNativeView";

// The AI-SEO OS is its own deployed microservice. It can be viewed two ways,
// remembered per user:
//  • Embedded  — the full AI-SEO app in an iframe, single-signed-on via a token
//    the CRM mints (no second login), falling back to a plain load (manual
//    login) if SSO isn't configured yet.
//  • Native    — a curated CRM-native cockpit (health / sales / events /
//    provenance), for people who want the essentials without the full app.
const AISEO_URL = process.env.REACT_APP_AISEO_URL || "https://ai-seo.collings.com.au";

const AiSeoPage = () => {
  const [mode, setMode] = useState(null); // 'embedded' | 'native' (null = loading pref)
  const [embedSrc, setEmbedSrc] = useState(null);

  // Load the user's saved preference (defaults to embedded).
  useEffect(() => {
    let alive = true;
    api
      .get("/api/aiseo/view-mode/")
      .then((r) => { if (alive) setMode(r.data?.view_mode === "native" ? "native" : "embedded"); })
      .catch(() => { if (alive) setMode("embedded"); });
    return () => { alive = false; };
  }, []);

  // Build the iframe URL with a fresh single-use SSO token. If SSO isn't set up
  // (endpoint 503s), load the app plainly so the user can log in manually.
  const loadEmbed = useCallback(async () => {
    try {
      const r = await api.get("/api/aiseo/sso-token/");
      const base = (r.data?.embed_url || AISEO_URL).replace(/\/+$/, "");
      setEmbedSrc(r.data?.token ? `${base}/#sso_token=${encodeURIComponent(r.data.token)}` : base);
    } catch {
      setEmbedSrc(AISEO_URL);
    }
  }, []);

  useEffect(() => { if (mode === "embedded") loadEmbed(); }, [mode, loadEmbed]);

  const switchMode = async (next) => {
    if (next === mode) return;
    setMode(next);
    try { await api.patch("/api/aiseo/view-mode/", { view_mode: next }); }
    catch { toast.error("Couldn't save your AI-SEO view preference."); }
  };

  const btn = (m, Icon, label) => (
    <button
      onClick={() => switchMode(m)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
        mode === m ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-5.5rem)]">
      <PageHeader
        title="AI-SEO"
        subtitle="Publish first-party property content grounded in the CRM Property Brain."
        action={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
              {btn("embedded", Monitor, "Full app")}
              {btn("native", LayoutGrid, "Native")}
            </div>
            <a
              href={AISEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              New tab
            </a>
          </div>
        }
      />

      {mode === "native" ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <AiSeoNativeView onOpenFullApp={() => switchMode("embedded")} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {embedSrc && (
            <iframe
              key={embedSrc}
              src={embedSrc}
              title="AI-SEO"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="clipboard-write"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AiSeoPage;
